// SIMPLIFIED ACTIVE RENTALS - USING ONLY RESERVATIONS DATA SOURCE
// Enhanced with SHARED Invoice Service for Extended Invoices
// WITH RESERVATION NUMBER DISPLAY (BK693061)

import React, { useState, useEffect } from "react";
import { 
  CheckCircle, ChevronDown, ChevronUp, Info, User, FileText, MapPin, 
  AlertCircle, X, Shield, Building, Database, Bike, Loader, Clock,
  Trash2, RefreshCw, CheckCircle2, AlertTriangle
} from "lucide-react";
import { 
  collection, 
  query, 
  where, 
  onSnapshot,
  getDocs,
  Timestamp,
  updateDoc,
  deleteDoc,
  doc,
  addDoc,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase';

// Import the SHARED Firebase Invoice Service
import {
  addInvoice,
  generateInvoiceNumber,
  setCompanyContext
} from '../services/firebaseInvoiceService';

// Simplified ActiveRentals Component - Uses Only Reservations Collection
const SimplifiedActiveRentals = ({ 
  currentUser,
  currentCompany, 
  getCompanyContext
}) => {
  // State Management
  const [activeReservations, setActiveReservations] = useState([]);
  const [managedBikes, setManagedBikes] = useState([]);
  const [bikeDetailsMap, setBikeDetailsMap] = useState(new Map());
  const [activeRentalFilter, setActiveRentalFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [phoneSearchTerm, setPhoneSearchTerm] = useState("");
  const [reservationNumberSearch, setReservationNumberSearch] = useState(""); // 🎫 NEW: Reservation number search
  const [selectedBikeModel, setSelectedBikeModel] = useState("all");
  const [selectedBike, setSelectedBike] = useState("all");
  const [dateFilter, setDateFilter] = useState("all");
  const [expandedRental, setExpandedRental] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [operationStatus, setOperationStatus] = useState(null);

  const companyId = getCompanyContext();

  // Firebase Collections - SIMPLIFIED: Only using reservations and managed bikes
  const reservationsCollection = collection(db, `companies/${companyId}/reservations`);
  const managedBikesCollection = collection(db, 'managedBikes');
  const incomeCollection = collection(db, `companies/${companyId}/income`);

  // Load managed bikes for bike details
  useEffect(() => {
    if (!companyId) return;

    const q = query(managedBikesCollection, where('companyId', '==', companyId));
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const bikes = [];
      const detailsMap = new Map();
      
      querySnapshot.forEach((doc) => {
        const bikeData = { id: doc.id, ...doc.data() };
        bikes.push(bikeData);
        detailsMap.set(bikeData.regNumber, bikeData);
        detailsMap.set(bikeData.id, bikeData);
        detailsMap.set(bikeData.model, bikeData);
      });
      
      setManagedBikes(bikes);
      setBikeDetailsMap(detailsMap);
    });

    return () => unsubscribe();
  }, [companyId]);

  // SIMPLIFIED: Subscribe to reservations only (no motorbike-rentals)
  useEffect(() => {
    if (!companyId) return;
    
    // Only get booked reservations (these are the "active rentals")
    const q = query(
      reservationsCollection, 
      where('availabilityStatus', 'in', ['booked'])
    );
    
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const reservations = [];
      querySnapshot.forEach((doc) => {
        const reservation = { id: doc.id, ...doc.data() };
        
        // Calculate dynamic status based on dates
        reservation.calculatedStatus = calculateDynamicStatus(reservation);
        
        // Only include if it's actually active (not cancelled, not completed)
        if (reservation.availabilityStatus !== 'cancel' && 
            reservation.calculatedStatus !== 'Completed') {
          reservations.push(reservation);
        }
      });
      // Sort by creation date - newest first (top of list)
      reservations.sort((a, b) => {
        const dateA = a.createdAt?.toDate?.() || a.createdAt || new Date(0);
        const dateB = b.createdAt?.toDate?.() || b.createdAt || new Date(0);
        return dateB - dateA; // Descending order (newest first)
      });
      
      
      setActiveReservations(reservations);
      setIsInitialLoading(false);
    });

    return () => unsubscribe();
  }, [companyId]);

  // UTILITY FUNCTIONS

  const calculateDynamicStatus = (reservation) => {
    if (!reservation.pickupDateTime || !reservation.dropoffDateTime) {
      return reservation.availabilityStatus || "Unknown";
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const startDate = new Date(reservation.pickupDateTime);
    startDate.setHours(0, 0, 0, 0);
    
    const endDate = new Date(reservation.dropoffDateTime);
    endDate.setHours(0, 0, 0, 0);

    // Check for manual completion
    if (reservation.rentalStatus === 'completed' || reservation.rentalStatus === 'returned') {
      return 'Completed';
    }

    // Check for manual active status
    if (reservation.rentalStatus === 'active') {
      return 'Active';
    }

    if (today < startDate) {
      return "Reserved";
    } else if (today >= startDate && today <= endDate) {
      return "Active";
    } else if (today > endDate) {
      return "Overdue";
    }

    return "Reserved";
  };

  const checkOverdueStatus = (reservation) => {
    if (!reservation.dropoffDateTime) return { isOverdue: false, daysOverdue: 0 };
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const endDate = new Date(reservation.dropoffDateTime);
    endDate.setHours(0, 0, 0, 0);
    
    if (today > endDate) {
      const diffTime = today - endDate;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return { isOverdue: true, daysOverdue: diffDays };
    }
    
    return { isOverdue: false, daysOverdue: 0 };
  };

  const getBikeDetails = (bikeReference) => {
    const details = bikeDetailsMap.get(bikeReference) || 
                   Array.from(bikeDetailsMap.values()).find(bike => 
                     bike.regNumber === bikeReference ||
                     bike.model === bikeReference ||
                     bike.id === bikeReference
                   );
    
    if (details) {
      return {
        model: details.model,
        regNumber: details.regNumber,
        dailyRate: details.dailyRate || 25,
        color: details.color,
        year: details.year,
        isManaged: true
      };
    }
    
    return {
      model: bikeReference || 'Unknown Model',
      regNumber: bikeReference || 'Unknown Registration',
      dailyRate: 25,
      isManaged: false
    };
  };

  // Helper function to get unique bike models
  const getUniqueBikeModels = () => {
    const models = managedBikes.map(bike => bike.model).filter(Boolean);
    const uniqueModels = [...new Set(models)];
    return uniqueModels.sort();
  };

  // Helper function to filter bikes by selected model
  const getFilteredBikesByModel = () => {
    if (selectedBikeModel === "all") return managedBikes;
    return managedBikes.filter(bike => bike.model === selectedBikeModel);
  };

  // Handler for bike model change
  const handleBikeModelChange = (model) => {
    setSelectedBikeModel(model);
    setSelectedBike("all"); // Reset bike selection when model changes
  };

  // ENHANCED RETURN FUNCTIONS WITH SHARED INVOICE SERVICE AND RESERVATION NUMBER

  const createExtendedRentalInvoice = async (reservation, daysOverdue) => {
    const bikeDetails = getBikeDetails(reservation.motorbikeNumber);
    const extendedAmount = bikeDetails.dailyRate * daysOverdue;
    
    // Set company context for the shared invoice service
    setCompanyContext(companyId, currentCompany.name);
    
    // Generate invoice number using SHARED service
    const invoiceNumber = await generateInvoiceNumber(companyId);
    
    console.log('📄 Creating extended invoice using SHARED firebaseInvoiceService');
    console.log('✅ Generated invoice number:', invoiceNumber);
    console.log('🎫 Reservation number:', reservation.reservationNumber); // Log reservation number
    
    const today = new Date();
    const endDate = new Date(reservation.dropoffDateTime);
    const extendedStartDate = new Date(endDate);
    extendedStartDate.setDate(extendedStartDate.getDate() + 1);
    
    // Create invoice data using SHARED service format WITH RESERVATION NUMBER
    const invoiceData = {
      invoiceNumber,
      reservationNumber: reservation.reservationNumber, // 🎫 INCLUDE RESERVATION NUMBER
      reservationId: reservation.id,
      customerName: reservation.customerName || "Unknown Customer",
      customerEmail: reservation.email || '',
      customerPhone: reservation.whatsappNumber || '',
      email: reservation.email || '',
      whatsappNumber: reservation.whatsappNumber || '',
      licenseNumber: reservation.licenseNumber || '',
      motorbikeNumber: reservation.motorbikeNumber,
      bikeModel: `${bikeDetails.model} (${bikeDetails.regNumber})`,
      totalAmount: extendedAmount,
      rentalAmount: extendedAmount,
      addonCosts: 0,
      discountAmount: 0,
      depositAmount: '0',
      pickupDateTime: extendedStartDate.toISOString(),
      dropoffDateTime: today.toISOString(),
      pickupLocation: reservation.pickupLocation || 'office',
      selectedAddons: [],
      paymentMethod: reservation.paymentMethod || 'Cash',
      rentalPaymentStatus: 'pending',
      status: 'generated',
      note: `Extended rental charges - ${daysOverdue} days overdue. Original end date: ${endDate.toLocaleDateString()}. Booking reference: ${reservation.reservationNumber || 'N/A'}`,
      isExtendedRental: true,
      type: 'extended',
      automaticallyGenerated: true,
      originalReservationId: reservation.id,
      originalReturnDate: reservation.dropoffDateTime,
      daysOverdue: daysOverdue,
      dailyRateApplied: bikeDetails.dailyRate,
      createdByUsername: currentUser?.username || 'System',
      createdBy: currentUser?.id || 'system',
      sourceComponent: 'active-rentals',
      processedBy: currentUser?.username || 'System',
      processedFrom: 'ActiveRentals'
    };
    
    console.log('💾 Saving extended invoice to SHARED collection with company context:', companyId);
    console.log('🎫 Invoice includes booking reference:', reservation.reservationNumber);
    
    // Use the SHARED addInvoice service
    const invoiceId = await addInvoice(invoiceData, companyId, currentCompany.name);
    
    console.log('✅ Extended invoice created successfully with ID:', invoiceId);
    console.log('✅ Invoice will now appear in BOTH Invoice Management AND Finance Manager');
    console.log('🎫 Invoice linked to booking:', reservation.reservationNumber);
    
    // Still record income entry in company-specific collection
    const incomeEntry = {
      invoice: invoiceNumber,
      amount: extendedAmount,
      date: today.toISOString().split('T')[0],
      description: `Extended Rental - ${reservation.customerName} (${daysOverdue} days overdue) - Booking: ${reservation.reservationNumber || 'N/A'}`,
      type: 'extended',
      isExtendedRental: true,
      reservationNumber: reservation.reservationNumber, // 🎫 Include in income entry
      companyId: companyId,
      processedBy: currentUser?.username || 'Unknown',
      createdAt: serverTimestamp()
    };
    
    await addDoc(incomeCollection, incomeEntry);
    
    return invoiceNumber;
  };

  const returnBikeToFleet = async (bikeReference) => {
    try {
      const bikesQuery = query(managedBikesCollection, where('companyId', '==', companyId));
      const bikesSnapshot = await getDocs(bikesQuery);
      
      for (const bikeDoc of bikesSnapshot.docs) {
        const bikeData = bikeDoc.data();
        
        if (bikeData.regNumber === bikeReference || 
            bikeData.model === bikeReference || 
            bikeDoc.id === bikeReference) {
          
          await updateDoc(doc(db, 'managedBikes', bikeDoc.id), {
            status: 'Available',
            currentRental: null,
            rentedBy: null,
            rentedUntil: null,
            lastReturnedAt: Timestamp.now(),
            returnProcessedBy: currentUser?.username || 'Unknown'
          });
          
          return { success: true, bikeModel: bikeData.model, bikeReg: bikeData.regNumber };
        }
      }
      
      return { success: false, error: 'Bike not found in fleet' };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  // SIMPLIFIED RETURN FUNCTION - WITH RESERVATION NUMBER IN CONFIRMATIONS
  const handleSimplifiedBikeReturn = async (reservation) => {
    const { isOverdue, daysOverdue } = checkOverdueStatus(reservation);
    const bikeDetails = getBikeDetails(reservation.motorbikeNumber);
    
    // 🎫 ENHANCED CONFIRMATION MESSAGE WITH RESERVATION NUMBER
    let confirmMessage = `Complete bike return?\n\n`;
    
    // Show booking number prominently if available
    if (reservation.reservationNumber) {
      confirmMessage += `📄 Booking #: ${reservation.reservationNumber}\n`;
      confirmMessage += `━━━━━━━━━━━━━━━━━━━━━━\n`;
    }
    
    confirmMessage += `Customer: ${reservation.customerName || 'Unknown'}\n`;
    confirmMessage += `Bike: ${bikeDetails.model} (${bikeDetails.regNumber})\n`;
    
    if (isOverdue) {
      confirmMessage += `\nSTATUS: ${daysOverdue} days OVERDUE\n`;
      confirmMessage += `Extended charges: $${(bikeDetails.dailyRate * daysOverdue).toFixed(2)}\n`;
    } else {
      confirmMessage += `\nSTATUS: Returned on time\n`;
      confirmMessage += `No additional charges\n`;
    }
    
    confirmMessage += `\nThis will:\n`;
    confirmMessage += `• Mark reservation as completed\n`;
    confirmMessage += `• Return bike to available fleet\n`;
    if (isOverdue) {
      confirmMessage += `• Create extended rental invoice (via SHARED service)\n`;
      confirmMessage += `• Invoice will appear in Invoice Management & Finance Manager\n`;
      if (reservation.reservationNumber) {
        confirmMessage += `• Invoice will be linked to booking ${reservation.reservationNumber}\n`;
      }
    }
    
    if (!window.confirm(confirmMessage)) return;

    setIsLoading(true);
    setOperationStatus({ type: 'processing', message: 'Processing bike return...' });
    
    const operations = {
      reservationUpdated: false,
      bikeReturnedToFleet: false,
      extendedInvoiceCreated: false,
      errors: []
    };

    try {
      // Step 1: Update reservation status to completed
      setOperationStatus({ type: 'processing', message: 'Updating reservation status...' });
      
      const updateData = {
        rentalStatus: 'completed',
        availabilityStatus: 'completed', // This will remove it from active list
        returnedAt: new Date().toISOString(),
        returnedBy: currentUser?.username || 'Unknown',
        finalStatus: isOverdue ? 'returned_overdue' : 'returned_ontime',
        isOverdue: isOverdue,
        daysOverdue: daysOverdue || 0,
        updatedAt: serverTimestamp()
      };

      if (isOverdue) {
        updateData.extendedChargesApplied = true;
        updateData.extendedAmount = bikeDetails.dailyRate * daysOverdue;
      }

      await updateDoc(doc(reservationsCollection, reservation.id), updateData);
      operations.reservationUpdated = true;

      // Step 2: Return bike to fleet
      setOperationStatus({ type: 'processing', message: 'Returning bike to fleet...' });
      
      const bikeResult = await returnBikeToFleet(reservation.motorbikeNumber);
      if (bikeResult.success) {
        operations.bikeReturnedToFleet = true;
      } else {
        operations.errors.push(`Bike fleet update: ${bikeResult.error}`);
      }

      // Step 3: Create extended invoice if overdue (using SHARED service WITH RESERVATION NUMBER)
      if (isOverdue && daysOverdue > 0) {
        setOperationStatus({ 
          type: 'processing', 
          message: `Creating extended rental invoice${reservation.reservationNumber ? ` for booking ${reservation.reservationNumber}` : ''}...` 
        });
        
        try {
          const extendedInvoiceNumber = await createExtendedRentalInvoice(reservation, daysOverdue);
          operations.extendedInvoiceCreated = true;
          operations.extendedInvoiceNumber = extendedInvoiceNumber;
        } catch (error) {
          console.error('Extended invoice creation error:', error);
          operations.errors.push(`Extended invoice: ${error.message}`);
        }
      }

      // Show completion report WITH RESERVATION NUMBER
      showCompletionReport(operations, reservation, bikeDetails, isOverdue, daysOverdue);
      
      setOperationStatus({ 
        type: 'success', 
        message: 'Bike return completed successfully!' 
      });

    } catch (error) {
      console.error('Critical error in bike return:', error);
      setOperationStatus({ 
        type: 'error', 
        message: `Critical error: ${error.message}` 
      });
    } finally {
      setIsLoading(false);
      setTimeout(() => setOperationStatus(null), 5000);
    }
  };

  // ENHANCED COMPLETION REPORT WITH RESERVATION NUMBER
  const showCompletionReport = (operations, reservation, bikeDetails, isOverdue, daysOverdue) => {
    let report = `BIKE RETURN COMPLETED\n\n`;
    
    // 🎫 Show booking number prominently
    if (reservation.reservationNumber) {
      report += `📄 Booking Reference: ${reservation.reservationNumber}\n`;
      report += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
    }
    
    report += `Customer: ${reservation.customerName || 'Unknown'}\n`;
    report += `Bike: ${bikeDetails.model} (${bikeDetails.regNumber})\n`;
    report += `Return Type: ${isOverdue ? `OVERDUE (${daysOverdue} days)` : 'ON TIME'}\n\n`;
    
    report += `COMPLETED OPERATIONS:\n`;
    if (operations.reservationUpdated) {
      report += `✓ Reservation marked as 'completed'\n`;
    }
    if (operations.bikeReturnedToFleet) {
      report += `✓ Bike returned to fleet (AVAILABLE)\n`;
    }
    if (operations.extendedInvoiceCreated) {
      report += `✓ Extended invoice created: ${operations.extendedInvoiceNumber}\n`;
      if (reservation.reservationNumber) {
        report += `  📄 Linked to booking: ${reservation.reservationNumber}\n`;
      }
      report += `  (Using SHARED service - visible in Invoice Management & Finance Manager)\n`;
    }
    
    if (operations.errors.length > 0) {
      report += `\nISSUES ENCOUNTERED:\n`;
      operations.errors.forEach(error => {
        report += `⚠ ${error}\n`;
      });
    }
    
    report += `\nRESULT:\n`;
    report += `• Reservation completed and removed from active list\n`;
    report += `• Bike available for new reservations\n`;
    report += `• Complete transaction history preserved\n`;
    if (isOverdue) {
      report += `• Extended invoice synced across all systems\n`;
      if (reservation.reservationNumber) {
        report += `• All records linked to booking ${reservation.reservationNumber}\n`;
      }
    }
    
    alert(report);
  };

  // PICKUP FUNCTION - WITH RESERVATION NUMBER
  const handlePickup = async (reservation) => {
    const bikeDetails = getBikeDetails(reservation.motorbikeNumber);
    
    // 🎫 ENHANCED CONFIRMATION WITH RESERVATION NUMBER
    let confirmMessage = `Mark rental as picked up?\n\n`;
    
    if (reservation.reservationNumber) {
      confirmMessage += `📄 Booking #: ${reservation.reservationNumber}\n`;
      confirmMessage += `━━━━━━━━━━━━━━━━━━━━━━\n`;
    }
    
    confirmMessage += `Customer: ${reservation.customerName}\n`;
    confirmMessage += `Bike: ${bikeDetails.model} (${bikeDetails.regNumber})`;
    
    if (window.confirm(confirmMessage)) {
      try {
        setIsLoading(true);
        
        await updateDoc(doc(reservationsCollection, reservation.id), {
          rentalStatus: 'active',
          pickedUpAt: new Date().toISOString(),
          pickedUpBy: currentUser?.username || 'Unknown',
          updatedAt: serverTimestamp()
        });
        
        let successMessage = 'Pickup confirmed! Rental is now active.';
        if (reservation.reservationNumber) {
          successMessage += `\n\n📄 Booking: ${reservation.reservationNumber}`;
        }
        alert(successMessage);
        
      } catch (error) {
        alert(`Error processing pickup: ${error.message}`);
      } finally {
        setIsLoading(false);
      }
    }
  };

  // UI FUNCTIONS
  const toggleRentalDetails = (reservation, index) => {
    const rentalKey = reservation.id || index;
    if (expandedRental?.id === rentalKey) {
      setExpandedRental(null);
    } else {
      setExpandedRental({ id: rentalKey, ...reservation });
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "Active": return "bg-green-900 text-green-400";
      case "Reserved": return "bg-indigo-900 text-indigo-400";
      case "Due Today": return "bg-amber-900 text-amber-400";
      case "Overdue": return "bg-red-900 text-red-400";
      case "Completed": return "bg-gray-900 text-gray-400";
      default: return "bg-gray-900 text-gray-400";
    }
  };

  // Filter reservations WITH RESERVATION NUMBER SEARCH
  const filteredReservations = activeReservations.filter(reservation => {
    // Status filter
    if (activeRentalFilter !== "all" && reservation.calculatedStatus !== activeRentalFilter) {
      return false;
    }
    
    // Search filter (customer name)
    if (searchTerm && !reservation.customerName?.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }
    
    // Phone number search filter
    if (phoneSearchTerm) {
      const phoneMatch = 
        reservation.whatsappNumber?.toLowerCase().includes(phoneSearchTerm.toLowerCase()) ||
        reservation.localNumber?.toLowerCase().includes(phoneSearchTerm.toLowerCase());
      if (!phoneMatch) return false;
    }
    
    // 🎫 RESERVATION NUMBER SEARCH FILTER
    if (reservationNumberSearch) {
      const reservationMatch = reservation.reservationNumber?.toLowerCase().includes(reservationNumberSearch.toLowerCase());
      if (!reservationMatch) return false;
    }
    
    // Bike model filter
    if (selectedBikeModel !== "all") {
      const bikeDetails = getBikeDetails(reservation.motorbikeNumber);
      if (bikeDetails.model !== selectedBikeModel) {
        return false;
      }
    }
    
    // Bike number filter (only applies if model is selected)
    if (selectedBike !== "all" && reservation.motorbikeNumber !== selectedBike) {
      return false;
    }
    
    // Date filter
    if (dateFilter !== "all" && reservation.pickupDateTime) {
      const pickupDate = new Date(reservation.pickupDateTime);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      if (dateFilter === "today") {
        pickupDate.setHours(0, 0, 0, 0);
        if (pickupDate.getTime() !== today.getTime()) return false;
      } else if (dateFilter === "thisWeek") {
        const weekAgo = new Date(today);
        weekAgo.setDate(weekAgo.getDate() - 7);
        if (pickupDate < weekAgo) return false;
      } else if (dateFilter === "thisMonth") {
        if (pickupDate.getMonth() !== today.getMonth() || pickupDate.getFullYear() !== today.getFullYear()) {
          return false;
        }
      }
    }
    
    return true;
  });

  if (isInitialLoading) {
    return (
      <div className="lg:col-span-3 bg-gray-800 rounded-xl shadow-lg border border-gray-700">
        <div className="p-6 flex items-center justify-center">
          <Loader className="animate-spin text-orange-500 mr-3" size={32} />
          <div>
            <h3 className="text-white font-medium">Loading Simplified Active Rentals</h3>
            <p className="text-gray-400 text-sm">Using reservations data source only...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="lg:col-span-3 bg-gray-800 rounded-xl shadow-lg overflow-hidden border border-gray-700">
      {/* Header */}
      <div className="p-6 border-b border-gray-700">
        <div className="flex justify-between items-start">
          <div>
            <h2 className="text-xl font-semibold text-white flex items-center">
              <CheckCircle className="w-5 h-5 mr-2 text-orange-400" />
              Simplified Active Rentals
              <span className="ml-2 text-xs bg-blue-600 text-white px-2 py-1 rounded-full">
                Single Data Source
              </span>
              <span className="ml-2 text-xs bg-green-600 text-white px-2 py-1 rounded-full">
                No Duplicates
              </span>
              <span className="ml-2 text-xs bg-orange-600 text-white px-2 py-1 rounded-full">
                Booking # Display
              </span>
            </h2>
            <p className="text-sm text-gray-400 mt-1">
              Using only reservations collection - simplified and reliable
            </p>
            <div className="mt-2 flex items-center text-blue-400 text-sm">
              <Building size={16} className="mr-1" />
              <span>{currentCompany?.name} • {activeReservations.length} active reservations</span>
            </div>
          </div>
        </div>
      </div>

      {/* Operation Status Banner */}
      {operationStatus && (
        <div className={`p-4 border-b border-gray-700 ${
          operationStatus.type === 'success' ? 'bg-green-900/30 border-green-700' :
          operationStatus.type === 'error' ? 'bg-red-900/30 border-red-700' :
          'bg-blue-900/30 border-blue-700'
        }`}>
          <div className="flex items-center">
            {operationStatus.type === 'processing' && <Loader className="animate-spin mr-2" size={16} />}
            {operationStatus.type === 'success' && <CheckCircle2 className="mr-2 text-green-400" size={16} />}
            {operationStatus.type === 'error' && <AlertTriangle className="mr-2 text-red-400" size={16} />}
            <span className={`text-sm ${
              operationStatus.type === 'success' ? 'text-green-300' :
              operationStatus.type === 'error' ? 'text-red-300' :
              'text-blue-300'
            }`}>
              {operationStatus.message}
            </span>
          </div>
        </div>
      )}

      <div className="p-6">
        {/* System Info Panel WITH RESERVATION NUMBER INFO */}
        <div className="mb-6 bg-blue-900/30 border border-blue-700 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Database className="text-blue-400 flex-shrink-0 mt-0.5" size={20} />
            <div className="flex-1">
              <h3 className="text-blue-300 font-medium mb-2">
                Simplified Data Architecture
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="text-blue-200">Data Source:</span>
                  <div className="text-gray-300">✓ Reservations collection only</div>
                  <div className="text-gray-400 text-xs">No motorbike-rentals duplication</div>
                </div>
                <div>
                  <span className="text-blue-200">Status Calculation:</span>
                  <div className="text-gray-300">✓ Dynamic from dates</div>
                  <div className="text-gray-400 text-xs">Reserved → Active → Overdue</div>
                </div>
                <div>
                  <span className="text-blue-200">Extended Invoices:</span>
                  <div className="text-gray-300">✓ SHARED invoice service</div>
                  <div className="text-gray-400 text-xs">Synced with Invoice Management</div>
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-blue-700">
                <span className="text-orange-300 font-medium">🎫 Booking Number Display:</span>
                <div className="text-gray-300 text-xs mt-1">All reservations show booking # (e.g., BK693061) for easy reference and tracking</div>
              </div>
            </div>
          </div>
        </div>


        {/* Enhanced Filter Controls WITH RESERVATION NUMBER SEARCH */}
        <div className="mb-6 bg-gray-700/50 p-4 rounded-lg border border-gray-600">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Search by Customer Name */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Search Customer Name
              </label>
              <input
                type="text"
                placeholder="Search by customer name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>

            {/* Search by Phone Number */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Search Phone Number
              </label>
              <input
                type="text"
                placeholder="Search by phone..."
                value={phoneSearchTerm}
                onChange={(e) => setPhoneSearchTerm(e.target.value)}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>

            {/* 🎫 SEARCH BY RESERVATION NUMBER - NEW */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2 flex items-center gap-1">
                <FileText size={14} className="text-orange-400" />
                Search Booking #
              </label>
              <input
                type="text"
                placeholder="Search by booking # (BK693061)..."
                value={reservationNumberSearch}
                onChange={(e) => setReservationNumberSearch(e.target.value)}
                className="w-full px-3 py-2 bg-gray-800 border border-orange-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>

            {/* Filter by Bike Model - Step 1 */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Step 1: Select Bike Model
              </label>
              <select
                value={selectedBikeModel}
                onChange={(e) => handleBikeModelChange(e.target.value)}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                <option value="all">All Models</option>
                {getUniqueBikeModels().map(model => (
                  <option key={model} value={model}>
                    {model}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Second Row - Bike Number and Date Filter */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4">
            {/* Filter by Bike Number - Step 2 */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Step 2: Select Bike Number
              </label>
              <select
                value={selectedBike}
                onChange={(e) => setSelectedBike(e.target.value)}
                disabled={selectedBikeModel === "all"}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="all">
                  {selectedBikeModel === "all" ? "Select model first" : "All Bikes"}
                </option>
                {getFilteredBikesByModel().map(bike => (
                  <option key={bike.id} value={bike.regNumber}>
                    {bike.regNumber}
                  </option>
                ))}
              </select>
            </div>

            {/* Date Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Filter by Pickup Date
              </label>
              <select
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                <option value="all">All Dates</option>
                <option value="today">Today</option>
                <option value="thisWeek">This Week</option>
                <option value="thisMonth">This Month</option>
              </select>
            </div>
          </div>

          {/* Clear Filters Button */}
          <div className="mt-4 flex justify-end">
            <button
              onClick={() => {
                setSearchTerm("");
                setPhoneSearchTerm("");
                setReservationNumberSearch(""); // 🎫 Clear reservation number search
                setSelectedBikeModel("all");
                setSelectedBike("all");
                setDateFilter("all");
                setActiveRentalFilter("all");
              }}
              className="px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-lg text-sm transition-colors flex items-center gap-2"
            >
              <RefreshCw size={16} />
              Clear All Filters
            </button>
          </div>
        </div>

        {/* Status Filters */}
        <div className="flex space-x-2 mb-6 overflow-x-auto">
          {['all', 'Active', 'Reserved', 'Due Today', 'Overdue'].map(filter => (
            <button 
              key={filter}
              onClick={() => setActiveRentalFilter(filter)}
              className={`px-3 py-1 rounded-full text-sm whitespace-nowrap ${
                activeRentalFilter === filter 
                  ? "bg-gray-600 text-white" 
                  : "bg-gray-700 text-gray-300 hover:bg-gray-600"
              }`}
            >
              {filter === 'all' ? 'All' : filter} ({
                filter === 'all' 
                  ? activeReservations.length 
                  : activeReservations.filter(r => r.calculatedStatus === filter).length
              })
            </button>
          ))}
        </div>

        {/* Reservations Table WITH RESERVATION NUMBER COLUMN */}
        {filteredReservations.length === 0 ? (
          <div className="text-center py-8 text-gray-400">
            <Database className="mx-auto h-16 w-16 mb-4 opacity-50" />
            <p>No active reservations found</p>
            <p className="text-sm mt-2">
              {searchTerm || phoneSearchTerm || reservationNumberSearch || selectedBikeModel !== "all" || selectedBike !== "all" || dateFilter !== "all" 
                ? "Try adjusting your filters" 
                : "Completed reservations are marked as 'completed' status"}
            </p>
          </div>
        ) : (
          <div className="max-h-[600px] overflow-y-auto overflow-x-auto">
            <table className="min-w-full bg-gray-700 rounded-lg overflow-hidden">
              <thead className="bg-gray-900">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-orange-300 uppercase flex items-center gap-1">
                    <FileText size={14} />
                    Booking #
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Vehicle</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Customer</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Contact</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Period</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Actions</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-300 uppercase">Details</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800">
                {filteredReservations.map((reservation, index) => {
                  const bikeDetails = getBikeDetails(reservation.motorbikeNumber);
                  const isExpanded = expandedRental?.id === reservation.id;
                  const { isOverdue, daysOverdue } = checkOverdueStatus(reservation);
                  
                  return (
                    <React.Fragment key={reservation.id}>
                      <tr className={`bg-gray-700 hover:bg-gray-600 ${isExpanded ? 'bg-gray-600' : ''}`}>
                        {/* 🎫 BOOKING NUMBER COLUMN */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          {reservation.reservationNumber ? (
                            <div className="flex items-center gap-2 bg-gradient-to-r from-orange-500/20 to-red-500/20 border border-orange-500/40 rounded px-2 py-1">
                              <FileText className="w-3 h-3 text-orange-400 flex-shrink-0" />
                              <span className="text-xs text-orange-400 font-bold">
                                {reservation.reservationNumber}
                              </span>
                            </div>
                          ) : (
                            <span className="text-xs text-gray-500">N/A</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div>
                              <div className="text-sm font-medium text-white">{bikeDetails.model}</div>
                              <div className="text-xs text-gray-400">{bikeDetails.regNumber}</div>
                            </div>
                            {bikeDetails.isManaged && (
                              <span className="ml-2 text-xs bg-green-600 text-white px-2 py-1 rounded-full">
                                Managed
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-white">{reservation.customerName || "Unknown"}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-xs text-gray-300">
                            {reservation.whatsappNumber || reservation.localNumber || "N/A"}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-300">
                            {reservation.pickupDateTime && new Date(reservation.pickupDateTime).toLocaleDateString()} - {reservation.dropoffDateTime && new Date(reservation.dropoffDateTime).toLocaleDateString()}
                          </div>
                          {isOverdue && (
                            <div className="text-xs text-red-400 mt-1">
                              {daysOverdue} days overdue
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadgeClass(reservation.calculatedStatus)}`}>
                            {reservation.calculatedStatus}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {reservation.calculatedStatus === 'Reserved' && (
                            <button 
                              onClick={() => handlePickup(reservation)}
                              className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white rounded mr-2"
                              disabled={isLoading}
                            >
                              Pickup
                            </button>
                          )}
                          
                          {(reservation.calculatedStatus === 'Active' || reservation.calculatedStatus === 'Due Today' || reservation.calculatedStatus === 'Overdue') && (
                            <button 
                              onClick={() => handleSimplifiedBikeReturn(reservation)}
                              className={`px-3 py-1 text-white rounded ${
                                reservation.calculatedStatus === 'Overdue' 
                                  ? 'bg-red-600 hover:bg-red-700' 
                                  : 'bg-orange-600 hover:bg-orange-700'
                              }`}
                              disabled={isLoading}
                            >
                              {reservation.calculatedStatus === 'Overdue' ? 'Return (Extended)' : 'Return'}
                            </button>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <button 
                            onClick={() => toggleRentalDetails(reservation, index)}
                            className="text-orange-400 hover:text-orange-300"
                            disabled={isLoading}
                          >
                            {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                          </button>
                        </td>
                      </tr>
                      
                      {/* Expanded Details WITH RESERVATION NUMBER */}
                      {isExpanded && (
                        <tr className="bg-gray-800">
                          <td colSpan="8" className="px-6 py-4">
                            <div className="p-4 rounded-lg border border-gray-600">
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                <div>
                                  <h4 className="text-orange-400 text-sm font-medium mb-3 flex items-center">
                                    <Bike className="w-4 h-4 mr-1" /> Vehicle Information
                                  </h4>
                                  <div className="space-y-2 text-sm">
                                    <div><span className="text-gray-400">Model:</span> <span className="text-white">{bikeDetails.model}</span></div>
                                    <div><span className="text-gray-400">Registration:</span> <span className="text-white">{bikeDetails.regNumber}</span></div>
                                    <div><span className="text-gray-400">Daily Rate:</span> <span className="text-white">${bikeDetails.dailyRate}</span></div>
                                  </div>
                                </div>
                                
                                <div>
                                  <h4 className="text-blue-400 text-sm font-medium mb-3 flex items-center">
                                    <User className="w-4 h-4 mr-1" /> Customer Details
                                  </h4>
                                  <div className="space-y-2 text-sm">
                                    <div><span className="text-gray-400">Name:</span> <span className="text-white">{reservation.customerName || "Unknown"}</span></div>
                                    {reservation.whatsappNumber && (
                                      <div><span className="text-gray-400">WhatsApp:</span> <span className="text-white">{reservation.whatsappNumber}</span></div>
                                    )}
                                    {reservation.localNumber && (
                                      <div><span className="text-gray-400">Local:</span> <span className="text-white">{reservation.localNumber}</span></div>
                                    )}
                                    {reservation.email && (
                                      <div><span className="text-gray-400">Email:</span> <span className="text-white">{reservation.email}</span></div>
                                    )}
                                  </div>
                                </div>
                                
                                <div>
                                  <h4 className="text-green-400 text-sm font-medium mb-3 flex items-center">
                                    <FileText className="w-4 h-4 mr-1" /> Reservation Information
                                  </h4>
                                  <div className="space-y-2 text-sm">
                                    {/* 🎫 BOOKING NUMBER IN EXPANDED VIEW */}
                                    {reservation.reservationNumber && (
                                      <div className="mb-2 p-2 bg-gradient-to-r from-orange-500/20 to-red-500/20 border border-orange-500/40 rounded">
                                        <span className="text-orange-300 font-medium">Booking #:</span>
                                        <span className="text-orange-400 font-bold ml-2">{reservation.reservationNumber}</span>
                                      </div>
                                    )}
                                    <div><span className="text-gray-400">ID:</span> <span className="text-white">{reservation.id}</span></div>
                                    <div><span className="text-gray-400">Amount:</span> <span className="text-white">${reservation.rentalAmount || '0'}</span></div>
                                    <div><span className="text-gray-400">Status:</span> <span className={`px-2 py-1 rounded-full text-xs ${getStatusBadgeClass(reservation.calculatedStatus)}`}>{reservation.calculatedStatus}</span></div>
                                    <div><span className="text-gray-400">Invoice Service:</span> <span className="text-green-400">SHARED (Synced)</span></div>
                                  </div>
                                </div>
                              </div>
                              
                              {isOverdue && (
                                <div className="mt-4 p-3 bg-red-900/20 border border-red-700 rounded-lg">
                                  <div className="flex items-center gap-2 mb-2">
                                    <AlertCircle className="text-red-400" size={16} />
                                    <span className="text-red-300 font-medium">Overdue Rental</span>
                                    {reservation.reservationNumber && (
                                      <span className="text-orange-400 text-xs">
                                        (Booking: {reservation.reservationNumber})
                                      </span>
                                    )}
                                  </div>
                                  <div className="text-sm text-red-200">
                                    This rental is {daysOverdue} days overdue. Extended charges of ${(bikeDetails.dailyRate * daysOverdue).toFixed(2)} will apply upon return.
                                    <br />
                                    <span className="text-xs text-red-300 mt-1 inline-block">
                                      Extended invoice will be created using SHARED service and synced to Invoice Management & Finance Manager
                                      {reservation.reservationNumber && ` (linked to booking ${reservation.reservationNumber})`}
                                    </span>
                                  </div>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      
      {/* Loading Overlay */}
      {isLoading && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg p-6 flex items-center border border-gray-700">
            <Loader className="animate-spin h-6 w-6 border-b-2 border-orange-500 mr-3" />
            <span className="text-white">Processing reservation operation...</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default SimplifiedActiveRentals;